import { inject, Injectable } from '@angular/core';
import {
  IFormsProvider,
  ResourceData,
  ResourceState,
  LFOfflineOrder,
  LFOfflineOrderSourceType,
  LiveForm,
  SAVE_FORM_RESULT
} from '@lfw/roesberg.liveforms.core';
import {
  Observable,
  of,
  switchMap,
  map,
  catchError,
  Subject,
  finalize,
  concatMap,
  filter,
  tap,
  BehaviorSubject,
  shareReplay,
  withLatestFrom,
  firstValueFrom
} from 'rxjs';
import { ErrorHandlers } from '../../shared/helpers/error-handlers';
import { ActivatedRoute } from '@angular/router';
import { KeyValuePair } from '@lfw/roesberg.liveforms.core';
import { OfflineOrders, OfflineFormsClient } from './offline-forms.client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { NetworkService } from '../../network.service';
import {
  OFFLINE_ONLINE_DIALOG_RESULTS,
  StoreOfflineOrderOnlineDialogComponent
} from '../../shared/dialogs/store-offline-order-online-dialog/store-offline-order-online-dialog.component';
import { OnlineFormsClient } from '../online-orders/online-forms.client';
import { ResourceHelper } from '../../shared/helpers/resource-helper';

const UNKNOWN = 'unknown';

@Injectable()
export class OfflineFormsProvider implements IFormsProvider<LFOfflineOrder> {
  uploaded: KeyValuePair[] = [];
  pendingResourceUploads: ResourceData[] = [];
  saveQueue = new Subject<Observable<ResourceData>>();

  private items$ = new BehaviorSubject<OfflineOrders>({});
  offlineOrders$ = this.items$.asObservable().pipe(shareReplay(1), takeUntilDestroyed());

  private order: LFOfflineOrder | undefined;

  private readonly route = inject(ActivatedRoute);
  private readonly offlineFormClient = inject(OfflineFormsClient);
  private readonly dialog = inject(MatDialog);
  private readonly onlineFormsClient = inject(OnlineFormsClient);
  #networkService = inject(NetworkService);

  constructor() {
    this.saveQueue
      .pipe(
        finalize(() => console.log('stopped processing queue')),
        concatMap((save) => save)
      )
      .subscribe((resource) => {
        this.uploaded.push({ key: resource.saveId, value: resource.fileId });
      });
  }

  deleteOfflineFormResourceByFileId(fileId: string): void {
    if (!this.order) {
      return;
    }
    this.order.form.attachedResources = this.order.form.attachedResources.filter(
      (resource) => resource.fileId !== fileId
    );
  }

  saveForm(form: LiveForm): Observable<SAVE_FORM_RESULT> {
    if (!this.order) {
      return of(SAVE_FORM_RESULT.FAILED);
    }
    form.attachedResources = this.order.form.attachedResources;
    const resourceData = form.resourceData;
    const orderId = form.order?.id ?? this.order.id;

    const resourcesToDelete = ResourceHelper.filterResourcesByOrderAndState(
      resourceData,
      [ResourceState.Deleted],
      orderId
    ).map((resource) => resource.fileId);

    return of(form).pipe(
      withLatestFrom(this.#networkService.state$),
      switchMap(async ([_, networkState]) => {
        if (networkState === 'online' && this.order?.sourceType === LFOfflineOrderSourceType.FAILED_ONLINE_SAVE) {
          return await firstValueFrom(this.dialog.open(StoreOfflineOrderOnlineDialogComponent).afterClosed());
        }
        return this.order?.sourceType === LFOfflineOrderSourceType.FAILED_ONLINE_SAVE
          ? OFFLINE_ONLINE_DIALOG_RESULTS.KEEP_OFFLINE
          : OFFLINE_ONLINE_DIALOG_RESULTS.STORE_OFFLINE;
      }),
      switchMap(async (result) => {
        if (result !== OFFLINE_ONLINE_DIALOG_RESULTS.STORE_ONLINE) {
          const sourceType =
            result === OFFLINE_ONLINE_DIALOG_RESULTS.KEEP_OFFLINE
              ? LFOfflineOrderSourceType.FAILED_ONLINE_SAVE
              : LFOfflineOrderSourceType.USER_SELECTED;
          return await this.offlineFormClient.saveOfflineOrder(form, sourceType);
        } else {
          for (const resource of this.pendingResourceUploads) {
            const file = new File([resource.data], resource.fileId, { type: resource.data.type });
            const updated = await this.onlineFormsClient.uploadResource(resource, form.project.id, form.id, file);
            if (updated) {
              const idx = this.pendingResourceUploads.findIndex((r) => r.fileId === resource.fileId);
              if (idx >= 0) {
                this.pendingResourceUploads[idx] = updated;
              }
              const rdIdx = resourceData.findIndex((r) => r.fileId === resource.fileId);
              if (rdIdx >= 0) {
                resourceData[rdIdx] = updated;
              }
            }
          }
          const uploadedResourceMappings = ResourceHelper.filterResourcesByOrderAndState(
            resourceData,
            [ResourceState.Uploaded],
            orderId
          ).map((resource) => KeyValuePair.fromValues(resource.saveId, resource.fileId));

          return firstValueFrom(this.onlineFormsClient.saveForm(form, resourcesToDelete, uploadedResourceMappings));
        }
      })
    );
  }

  checkInOfflineOrder(order: LFOfflineOrder): Observable<SAVE_FORM_RESULT> {
    return of(order).pipe(
      withLatestFrom(this.#networkService.state$),
      switchMap(async ([_, networkState]) => {
        if (networkState !== 'online' || order.sourceType !== LFOfflineOrderSourceType.USER_SELECTED) {
          // don't checkin offline orders that where not explicitly checked out by the user before
          // maybe we have to overthink this logic later
          return SAVE_FORM_RESULT.FAILED;
        }
        const form = order.form;
        const orderId = order.id;
        const storedFormResources = await Promise.all(
          form.attachedResources.map((resource) => firstValueFrom(this.offlineFormClient.loadResource(resource.fileId)))
        );

        const resourcesToDelete = ResourceHelper.filterResourcesByOrderAndState(
          storedFormResources,
          [ResourceState.Deleted],
          orderId
        ).map((resource) => resource.fileId);

        const pendingResourceUploads = ResourceHelper.filterResourcesByOrderAndState(
          storedFormResources,
          [ResourceState.PendingUpload],
          orderId
        );

        for (const resource of pendingResourceUploads) {
          const file = new File([resource.data], resource.fileId, { type: resource.data.type });
          const updated = await this.onlineFormsClient.uploadResource(resource, form.project.id, form.id, file);
          if (updated) {
            const idx = pendingResourceUploads.findIndex((r) => r.fileId === resource.fileId);
            if (idx >= 0) {
              pendingResourceUploads[idx] = updated;
            }
          }
        }

        const uploadedResourceMappings = ResourceHelper.filterResourcesByOrderAndState(
          pendingResourceUploads,
          [ResourceState.Uploaded],
          orderId
        ).map((resource) => KeyValuePair.fromValues(resource.saveId, resource.fileId));

        await Promise.allSettled(
          storedFormResources.map(({ fileId }) => this.offlineFormClient.deleteResourceByFileId(fileId))
        );

        form.attachedResources = [];

        return firstValueFrom(
          this.onlineFormsClient.saveFormAndCheckIn(form, resourcesToDelete, uploadedResourceMappings)
        );
      })
    );
  }

  async saveFormResource(data: ResourceData): Promise<void> {
    if (data.data instanceof File) {
      const buffer = await data.data.arrayBuffer();
      data.data = new Blob([buffer], { type: data.data.type }) as Blob;
      //data.data.type; //do we need this line?
    }
    data.state = ResourceState.PendingUpload;
    this.saveQueue.next(this.upload(data));
  }

  upload(data: ResourceData): Observable<ResourceData> {
    this.pendingResourceUploads.push(data);
    this.order?.form.attachedResources.push({
      dataType: UNKNOWN,
      fileId: data.fileId,
      name: UNKNOWN
    });
    return this.offlineFormClient.saveResource(data);
  }

  loadFormResource(fileId: string): Observable<Blob> {
    return this.offlineFormClient.loadResource(fileId).pipe(
      tap((resource) => {
        if (resource.state === ResourceState.PendingUpload) {
          this.pendingResourceUploads.push(resource);
        }
      }),
      map((resource) => resource.data)
    );
  }

  loadForm(_?: string): Observable<LFOfflineOrder> {
    return this.route.paramMap.pipe(
      map((params) => params.get('id')),
      filter((formId: string | null): formId is string => !!formId),
      switchMap(async (formId: string) => await this.offlineFormClient.getOfflineOrderById(formId)),
      tap((order) => (this.order = order)),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  // Note: Both loadForm and loadFormForEdit are required by the IFormsProvider interface.
  // For offline orders the logic for loading a form and loading it for edit is currently
  // identical, so they intentionally share the same implementation here. They are kept
  // as separate methods to satisfy the interface contract and to allow future divergence
  // if edit-specific loading behavior is introduced.
  loadFormForEdit(_: boolean): Observable<LFOfflineOrder> {
    return this.route.paramMap.pipe(
      map((params) => params.get('id')),
      filter((formId: string | null): formId is string => !!formId),
      switchMap(async (formId: string) => await this.offlineFormClient.getOfflineOrderById(formId)),
      tap((order) => (this.order = order)),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }
}
